import React from 'react';
import { Link } from 'react-router-dom';
import { LegalLayout } from '../components/legal/LegalLayout';

export default function PractComPrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" testId="practcom-privacy-page">
      <p>
        This Privacy Policy explains how The Oncall Bot LLC ("PractCom", "we", "us" or "our") collects, uses and
        protects information when dental practices, their staff, their patients and website visitors use PractCom
        (the "Service"). It should be read with our <Link to="/practcom/terms">Terms of Service</Link>.
      </p>

      <h2>1. Two kinds of information</h2>
      <ul>
        <li><strong>Practice and visitor information</strong>: information about dentists, office staff and visitors to our website. We control this information and handle it as described in this policy.</li>
        <li><strong>Patient information</strong>: information that a dental practice puts into PractCom about its patients, including protected health information ("PHI"). We process it only on behalf of that practice, as its business associate under HIPAA, and as set out in the Business Associate Agreement ("BAA") the practice signs. The practice decides how patient information is used. Patients with questions about their information should contact their dental practice.</li>
      </ul>

      <h2>2. Information we collect</h2>
      <h3>From practices and staff</h3>
      <ul>
        <li>Account details: name, work email, phone number, practice name, address, website, role and login information.</li>
        <li>Billing details: plan and payment history. Card numbers are handled by our payment processor; we do not store full card numbers.</li>
        <li>Support and feedback you send us, including chats with Dr. G and dictated visit notes.</li>
      </ul>
      <h3>About patients (on behalf of practices)</h3>
      <ul>
        <li>Name, cell phone number, email address, preferred language and contact preferences.</li>
        <li>Treatment-related information a practice chooses to send or record, such as procedures, post-operative instructions, consents and refusals, signatures, Med Cards, check-in replies, referrals and documents.</li>
        <li>Message and portal activity, such as when a message was sent, delivered, opened or signed.</li>
      </ul>
      <h3>From website visitors</h3>
      <ul>
        <li>Information you enter in forms, such as a trial sign-up or a call booking.</li>
        <li>Device and usage data, such as browser type, pages viewed, referring page and ad campaign details (for example UTM tags or click IDs). We use cookies and similar tools, including Meta Pixel and Microsoft Clarity, to measure our own advertising and improve our website.</li>
      </ul>

      <h2>3. How we use information</h2>
      <ul>
        <li>To provide the Service: send texts and emails a practice requests, host the patient portal, store records and produce the documents a practice asks for.</li>
        <li>To run AI features a practice uses, such as Dictate Visit, Dr. G, translations, referral letters and SmileDesign images.</li>
        <li>To manage accounts, trials, billing and usage alerts.</li>
        <li>To provide support, keep the Service secure, prevent abuse and keep audit logs.</li>
        <li>To improve our website and measure our marketing. We never use patient information for advertising.</li>
        <li>To meet legal obligations.</li>
      </ul>

      <h2>4. How we share information</h2>
      <p>We do not sell personal information or PHI. We share information only:</p>
      <ul>
        <li><strong>With service providers</strong> that help us run PractCom, such as cloud hosting, text message delivery, email delivery, payment processing, AI processing and website analytics. Providers that handle PHI are bound by business associate agreements or equivalent written protections and may use it only to provide services to us.</li>
        <li><strong>Within a practice</strong>: authorized staff of a practice can see that practice's patient information. One practice cannot see another practice's data.</li>
        <li><strong>With others the practice directs</strong>, for example a referral letter to a specialist the practice chooses.</li>
        <li><strong>For legal reasons</strong>, when required by law, subpoena or court order, or to protect the rights and safety of patients, practices or the public.</li>
        <li><strong>In a business transfer</strong>, such as a merger or acquisition, subject to the same protections.</li>
      </ul>

      <h2>5. Text messages</h2>
      <p>
        Practices are responsible for getting patients' permission before texting them. Patients can reply
        <strong> STOP</strong> to any PractCom text to stop texts from that practice, and <strong>HELP</strong> for help.
        Message and data rates may apply. Mobile phone numbers and text consent are never shared with third parties for
        their own marketing.
      </p>

      <h2>6. Security</h2>
      <p>
        We protect information with encryption in transit and at rest, access controls, individual logins, audit logging
        and secure hosting. No system is perfectly secure. If a breach affects PHI, we notify the affected practice as
        required by HIPAA and the BAA so it can meet its own notification duties.
      </p>

      <h2>7. How long we keep information</h2>
      <p>
        We keep practice account information for as long as the account is active and as needed for billing, legal and
        audit purposes afterwards. Patient information is kept for as long as the practice keeps its account, or as
        required by law and the BAA. When a practice closes its account, it can ask us to return or delete its data.
        Backup copies are deleted on a regular schedule.
      </p>

      <h2>8. Your choices and rights</h2>
      <ul>
        <li>Practices and staff can view and update account details in the app, and can contact us to access, correct, export or delete their information.</li>
        <li>Patients should contact their dental practice to see, correct or get a copy of their records. We will help the practice respond.</li>
        <li>Depending on where you live, you may have additional rights under state privacy laws. Email us to make a request; we will not treat you differently for doing so.</li>
        <li>You can control cookies in your browser settings, and opt out of our marketing emails using the link in each email.</li>
      </ul>

      <h2>9. Children</h2>
      <p>
        PractCom is a service for dental practices and is not directed to children. A practice may store information about
        a patient who is a minor as part of that patient's care, with a parent or guardian as the contact. We do not
        knowingly collect information directly from children under 13.
      </p>

      <h2>10. Where information is stored</h2>
      <p>PractCom is operated from the United States, and information is stored and processed in the United States.</p>

      <h2>11. Changes to this policy</h2>
      <p>
        We may update this policy from time to time. If we make a material change, we will notify practices by email or in
        the app before it takes effect, and update the "Last updated" date above.
      </p>
    </LegalLayout>
  );
}
